/**
 * Première configuration — enregistrement de la passkey initiale.
 *
 * Cet écran n'existe que tant qu'aucun compte n'a été créé : `/api/auth/status`
 * bascule `bootstrapRequired` à `false` définitivement ensuite, et les routes
 * d'amorçage répondent alors 410.
 *
 * Le jeton demandé ici n'est **pas** un mot de passe à retenir : il est tiré au
 * hasard par le serveur à chaque démarrage tant que le compte n'existe pas, et
 * publié dans le seul journal système. Le raisonnement complet est dans
 * `server/src/auth/webauthn.ts`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { BootstrapResponse } from '../../../../shared/types';
import { guessDeviceLabel, runBootstrap } from '../../api/auth';
import { ApiError } from '../../api/client';
import { Button, ErrorNote, Field, Hint } from '../../components/ui/primitives';
import { TOKENS } from '../../components/ui/tokens';
import { AuthShell, RecoveryKeyPanel } from './parts';

const JOURNAL_COMMAND = "sudo journalctl -u nutri-api | grep -A4 \"jeton d'amorçage\"";

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  // WebAuthn rejette avec une DOMException lorsque l'utilisateur annule ou que
  // la plateforme refuse (domaine incohérent, pas d'authentificateur).
  if (error instanceof Error) return error.message;
  return 'Échec inattendu.';
}

export function BootstrapScreen() {
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [deviceLabel, setDeviceLabel] = useState(guessDeviceLabel);
  const [created, setCreated] = useState<BootstrapResponse | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const bootstrap = useMutation({
    mutationFn: () => runBootstrap(token.trim(), deviceLabel.trim()),
    onSuccess: (result) => {
      setCreated(result);
      setToken('');
    },
  });

  // Deuxième temps : la clé de secours, affichée une seule fois.
  if (created) {
    return (
      <AuthShell title="Clé de secours">
        <RecoveryKeyPanel
          recoveryKey={created.recoveryKey}
          acknowledged={acknowledged}
          onAcknowledge={setAcknowledged}
        />
        <Button
          disabled={!acknowledged}
          onClick={() => {
            // La session est déjà ouverte par `bootstrap/verify` : il suffit de
            // relire l'état pour que la garde laisse passer.
            void queryClient.invalidateQueries({ queryKey: ['auth'] });
          }}
        >
          Entrer dans l’application
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Première configuration">
      <Hint>
        Aucun compte n’existe encore. Le serveur a tiré un jeton à usage unique et l’a écrit
        dans son journal. Le récupérer sur le VPS :
      </Hint>
      <code
        style={{
          display: 'block',
          padding: '0.75rem',
          borderRadius: '0.75rem',
          background: TOKENS.surface,
          border: `1px solid ${TOKENS.border}`,
          fontSize: '0.8rem',
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {JOURNAL_COMMAND}
      </code>

      <Field
        label="Jeton d’amorçage"
        value={token}
        onChange={setToken}
        placeholder="collé depuis le journal"
      />
      <Field
        label="Nom de cet appareil"
        value={deviceLabel}
        onChange={setDeviceLabel}
        placeholder="iPhone"
      />

      {bootstrap.isError && <ErrorNote>{describe(bootstrap.error)}</ErrorNote>}

      <Button
        disabled={token.trim() === '' || deviceLabel.trim() === '' || bootstrap.isPending}
        onClick={() => {
          bootstrap.mutate();
        }}
      >
        {bootstrap.isPending ? 'Création…' : 'Créer la passkey'}
      </Button>

      <Hint>
        Le système va demander Face ID, Touch ID ou le code de l’appareil : c’est la passkey qui
        se crée.
      </Hint>
    </AuthShell>
  );
}
